import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
const PAPER = "#FAF6EF";
/**
 * Creates "justified" rows.
 *
 * Photos keep their natural aspect ratio while each row
 * expands/shrinks enough to fill the available width.
 */
function buildRows(photos, containerWidth, targetRowHeight, gap) {
    if (!containerWidth || !photos.length) {
        return [];
    }
    const rows = [];
    let currentRow = [];
    let currentAspectRatio = 0;
    for (const photo of photos) {
        currentRow.push(photo);
        currentAspectRatio += photo.aspectRatio;
        const totalGap = gap * (currentRow.length - 1);
        const calculatedHeight = (containerWidth - totalGap) / currentAspectRatio;
        /*
         * Once the row gets close to our target height,
         * finish the row.
         */
        if (calculatedHeight <= targetRowHeight) {
            rows.push({
                photos: currentRow,
                height: calculatedHeight,
            });
            currentRow = [];
            currentAspectRatio = 0;
        }
    }
    /*
     * Handle the final row.
     *
     * We don't force it to become huge just because it has
     * fewer photos. Instead, let it stay around the target
     * height and leave the remaining space empty.
     */
    if (currentRow.length > 0) {
        const totalGap = gap * (currentRow.length - 1);
        const calculatedHeight = (containerWidth - totalGap) / currentAspectRatio;
        rows.push({
            photos: currentRow,
            height: Math.min(calculatedHeight, targetRowHeight),
        });
    }
    return rows;
}
export default function PortfolioPhotoGrid({ photos, targetRowHeight = 280, gap = 12, }) {
    const [loadedPhotos, setLoadedPhotos] = useState([]);
    const [containerWidth, setContainerWidth] = useState(0);
    /*
     * Track the width of the gallery so the layout
     * recalculates responsively.
     */
    useEffect(() => {
        const updateWidth = () => {
            const element = document.getElementById("portfolio-photo-grid");
            if (element) {
                setContainerWidth(element.clientWidth);
            }
        };
        updateWidth();
        window.addEventListener("resize", updateWidth);
        return () => window.removeEventListener("resize", updateWidth);
    }, []);
    /*
     * Load the natural dimensions of every photo.
     */
    useEffect(() => {
        let cancelled = false;
        const loadPhotos = async () => {
            const results = await Promise.all(photos.map((photo) => new Promise((resolve) => {
                const image = new Image();
                image.onload = () => {
                    resolve({
                        ...photo,
                        width: image.naturalWidth,
                        height: image.naturalHeight,
                        aspectRatio: image.naturalWidth / image.naturalHeight,
                    });
                };
                image.onerror = () => {
                    /*
                     * Ignore photos that fail to load.
                     */
                    resolve({
                        ...photo,
                        width: 1,
                        height: 1,
                        aspectRatio: 1,
                    });
                };
                image.src = photo.src;
            })));
            if (!cancelled) {
                setLoadedPhotos(results);
            }
        };
        loadPhotos();
        return () => {
            cancelled = true;
        };
    }, [photos]);
    const rows = useMemo(() => {
        if (!containerWidth || !loadedPhotos.length) {
            return [];
        }
        return buildRows(loadedPhotos, containerWidth, targetRowHeight, gap);
    }, [loadedPhotos, containerWidth, targetRowHeight, gap]);
    return (React.createElement(Box, { id: "portfolio-photo-grid", sx: {
            width: "100%",
            bgcolor: PAPER,
        } }, rows.map((row, rowIndex) => (React.createElement(Box, { key: rowIndex, sx: {
            display: "flex",
            gap: `${gap}px`,
            width: "100%",
            mb: rowIndex === rows.length - 1 ? 0 : `${gap}px`,
        } }, row.photos.map((photo) => {
        const width = photo.aspectRatio * row.height;
        return (React.createElement(Box, { key: photo.src, component: "img", src: photo.src, alt: photo.alt ?? "", loading: "lazy", sx: {
                display: "block",
                width: `${width}px`,
                height: `${row.height}px`,
                objectFit: "cover",
                flexShrink: 0,
            } }));
    }))))));
}
